import React, { useState, useEffect } from "react";
import { Row, Col, Form, Button, Table, InputGroup, Spinner } from "react-bootstrap";
import { useNavigate } from "react-router-dom";

import { _post } from "../../services/apiClient";
import { apiUrls } from "../../config/apiUrls.const";
import { showToast } from "../../services/ToastService";
import { useBusiness } from "../../contexts/BusinessContext";

// Explicit Domain Interfaces for Type Safety
interface ServiceItem {
  id: number;
  title: string;
  price: string | number;
}

interface SelectedService {
  business_item_id: number;
  title: string;
  quantity: number;
  price: number;
  total: number;
}

export default function CreateBooking(): React.JSX.Element {
  const navigate = useNavigate();
  const { business } = useBusiness();

  // --- Sub-States ---
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [catalogLoading, setCatalogLoading] = useState<boolean>(false);
  const [servicesCatalog, setServicesCatalog] = useState<ServiceItem[]>([]);

  // --- Payload Form State Parameters ---
  const [customerName, setCustomerName] = useState<string>("");
  const [mobileNo, setMobileNo] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [bookingType, setBookingType] = useState<"instant" | "scheduled">("scheduled");
  const [locationType, setLocationType] = useState<"center" | "home">("center");
  const [scheduledAt, setScheduledAt] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [address, setAddress] = useState<string>("");
  
  const [latitude, setLatitude] = useState<string>("23.8103"); 
  const [longitude, setLongitude] = useState<string>("90.4125");

  // --- Strongly Typed Cart State ---
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);

  // Load Inventory Catalog Metrics on Render Context Change
  useEffect(() => {
    let isMounted = true;
    async function loadCatalog(): Promise<void> {
      if (!business?.id) return;
      try {
        setCatalogLoading(true);
        const response = await _post(apiUrls.items, {
          business_id: business.id,
        });

        if (!isMounted) return;

        const extractedData = response?.data?.success !== undefined ? response.data.data : response?.data;
        if (Array.isArray(extractedData)) {
          setServicesCatalog(extractedData);
        } else if (extractedData?.data && Array.isArray(extractedData.data)) {
          setServicesCatalog(extractedData.data);
        }
      } catch (err) {
        console.error("Failed to parse services catalog baseline:", err);
        showToast({ icon: "error", title: "Catalog Error", text: "Could not load service inventory metrics." });
      } finally {
        if (isMounted) setCatalogLoading(false);
      }
    }
    loadCatalog();
    return () => { isMounted = false; };
  }, [business?.id]);

  useEffect(() => {
    if (bookingType === "instant") {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      setScheduledAt(now.toISOString().slice(0, 16));
    } else {
      setScheduledAt("");
    }
  }, [bookingType]);

  // Cartesian Allocation Actions
  const handleAddService = (serviceIdStr: string): void => {
    const serviceId = parseInt(serviceIdStr, 10);
    if (!serviceId) return;

    const matchedCatalogItem = servicesCatalog.find((item) => item.id === serviceId);
    if (!matchedCatalogItem) return;

    const exists = selectedServices.find((s) => s.business_item_id === serviceId);
    if (exists) {
      showToast({ icon: "warning", title: "Duplicate Entry", text: "Service item already added. Modify its operational quantity instead." });
      return;
    }

    const priceNum = typeof matchedCatalogItem.price === "string" ? parseFloat(matchedCatalogItem.price) : matchedCatalogItem.price;
    const newEntry: SelectedService = {
      business_item_id: matchedCatalogItem.id,
      title: matchedCatalogItem.title,
      quantity: 1,
      price: priceNum || 0,
      total: priceNum || 0,
    };

    setSelectedServices((prev) => [...prev, newEntry]);
  };

  const handleQuantityChange = (index: number, delta: number): void => {
    setSelectedServices((prev) => {
      const updated = [...prev];
      const targetItem = updated[index];
      const targetQty = targetItem.quantity + delta;

      if (targetQty <= 0) {
        updated.splice(index, 1);
      } else {
        updated[index] = {
          ...targetItem,
          quantity: targetQty,
          total: targetItem.price * targetQty,
        };
      }
      return updated;
    });
  };

  const calculateGrossSubtotal = (): number => {
    return selectedServices.reduce((acc, curr) => acc + curr.total, 0);
  };

  const handleSubmitBookingForm = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    if (!business?.id) {
      showToast({ icon: "error", title: "Context Conflict", text: "No operational profile active." });
      return;
    }

    if (!customerName || !mobileNo) {
      showToast({ icon: "error", title: "Validation Error", text: "Customer Name and Contact Mobile variables are mandatory." });
      return;
    }

    if (selectedServices.length === 0) {
      showToast({ icon: "error", title: "Cart Empty", text: "Please map at least one operational service item inside this request." });
      return;
    }

    if (locationType === "home" && !address) {
      showToast({ icon: "error", title: "Logistics Error", text: "Home environment configurations explicitly require an execution address description." });
      return;
    }

    if (!scheduledAt) {
      showToast({ icon: "error", title: "Timeline Missing", text: "Please select an operational execution timestamp target." });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        business_id: business.id,
        customer_name: customerName,
        mobile_no: mobileNo,
        email: email || null,
        booking_type: bookingType,
        location_type: locationType,
        scheduled_at: new Date(scheduledAt).toISOString(),
        notes: notes || null,
        address: locationType === "home" ? address : "Served directly at business center campus location setup.",
        latitude: locationType === "home" ? latitude : null,
        longitude: locationType === "home" ? longitude : null,
        status: 0, 
        items: selectedServices.map((item) => ({
          business_item_id: item.business_item_id,
          quantity: item.quantity,
          price: item.price,
        })),
      };

      const response = await _post(apiUrls.createBooking || "/api/bookings/create", payload);
      const isSuccess = response?.data?.success ?? response?.data?.success ?? false;

      if (isSuccess) {
        showToast({ icon: "success", title: "Booking Dispatched", text: "Digital operation tracking record written successfully." });
        navigate("/bookings");
      } else {
        showToast({ icon: "error", title: "Ingestion Failure", text: response?.data?.message || "Failed to commit record." });
      }
    } catch (err: any) {
      console.error(err);
      showToast({ icon: "error", title: "Server Error", text: err?.response?.data?.message || "Failed to write record." });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid p-0">
      <h1 className="h3 mb-3">
        Create Booking
        <Button className="float-end" variant="secondary" size="sm" onClick={() => navigate("/bookings")}>
          Back to Ledger
        </Button>
      </h1>

      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm">
            <div className="card-body p-4 border-0">
              
              <Form onSubmit={handleSubmitBookingForm}>
                <Row className="g-4">
                  {/* Left Parameter Inputs Column Stack */}
                  <Col lg={8}>
                    <div className="mb-4">
                      <h5 className="fw-bold text-dark text-uppercase small tracking-wide border-bottom pb-2 mb-3">
                        1. Customer Identification Profile
                      </h5>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small fw-semibold">Client / Customer Full Name *</Form.Label>
                            <Form.Control type="text" placeholder="e.g. Labib Ahmed" value={customerName} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomerName(e.target.value)} required className="shadow-none" />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small fw-semibold">Contact Mobile Number *</Form.Label>
                            <Form.Control type="tel" placeholder="e.g. 01856856236" value={mobileNo} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMobileNo(e.target.value)} required className="shadow-none" />
                          </Form.Group>
                        </Col>
                        <Col md={12}>
                          <Form.Group>
                            <Form.Label className="text-muted small fw-semibold">Email Address (Optional)</Form.Label>
                            <Form.Control type="email" placeholder="e.g. client@domain.com" value={email} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)} className="shadow-none" />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    <div>
                      <h5 className="fw-bold text-dark text-uppercase small tracking-wide border-bottom pb-2 mb-3">
                        2. Logistics & Environment Framework
                      </h5>
                      <Row className="g-3 mb-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small d-block fw-semibold">Execution Timeline Priority</Form.Label>
                            <div className="d-flex gap-3 mt-1">
                              <Form.Check type="radio" label="📅 Scheduled Appointment" name="booking_type" id="type_sched" checked={bookingType === "scheduled"} onChange={() => setBookingType("scheduled")} className="small" />
                              <Form.Check type="radio" label="🚨 Instant Dispatch" name="booking_type" id="type_inst" checked={bookingType === "instant"} onChange={() => setBookingType("instant")} className="small text-danger" />
                            </div>
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small d-block fw-semibold">Delivery Target Context</Form.Label>
                            <div className="d-flex gap-3 mt-1">
                              <Form.Check type="radio" label="🏢 Center Walk-in" name="location_type" id="loc_center" checked={locationType === "center"} onChange={() => setLocationType("center")} className="small" />
                              <Form.Check type="radio" label="🏠 Home Delivery" name="location_type" id="loc_home" checked={locationType === "home"} onChange={() => setLocationType("home")} className="small text-primary" />
                            </div>
                          </Form.Group>
                        </Col>
                      </Row>

                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="text-muted small fw-semibold">Execution Target Timestamp *</Form.Label>
                            <Form.Control type="datetime-local" value={scheduledAt} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setScheduledAt(e.target.value)} required readOnly={bookingType === "instant"} className="shadow-none" />
                          </Form.Group>
                        </Col>

                        {locationType === "home" && (
                          <Col md={12}>
                            <Form.Group className="mb-2">
                              <Form.Label className="text-muted small fw-semibold text-primary">Destination Service Address *</Form.Label>
                              <Form.Control as="textarea" rows={2} placeholder="Provide explicit local map coordinates or structured descriptions..." value={address} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)} required className="shadow-none" />
                            </Form.Group>
                            <Row className="g-2">
                              <Col xs={6}>
                                <Form.Control type="text" placeholder="Latitude" value={latitude} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLatitude(e.target.value)} className="shadow-none small font-monospace text-center" />
                              </Col>
                              <Col xs={6}>
                                <Form.Control type="text" placeholder="Longitude" value={longitude} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLongitude(e.target.value)} className="shadow-none small font-monospace text-center" />
                              </Col>
                            </Row>
                          </Col>
                        )}

                        <Col md={12}>
                          <Form.Group>
                            <Form.Label className="text-muted small fw-semibold">Operator Instructions / Notes</Form.Label>
                            <Form.Control as="textarea" rows={2} placeholder="Optional routing descriptions or processing instructions..." value={notes} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNotes(e.target.value)} className="shadow-none" />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>
                  </Col>

                  {/* Right Aggregate Mathematics Calculation Panel */}
                  <Col lg={4} className="border-start border-light ps-lg-4">
                    <h5 className="fw-bold text-dark text-uppercase small tracking-wide border-bottom pb-2 mb-3">
                      3. Service Inventory Cart
                    </h5>

                    <Form.Group className="mb-3">
                      <Form.Label className="text-muted small fw-semibold">Select Operational Catalog Asset</Form.Label>
                      <InputGroup size="sm">
                        <Form.Select 
                          defaultValue="" 
                          disabled={catalogLoading}
                          onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                            if(e.target.value) {
                              handleAddService(e.target.value);
                              e.target.value = "";
                            }
                          }}
                          className="shadow-none"
                        >
                          <option value="" disabled>{catalogLoading ? "Fetching inventory data..." : "-- Choose Service Asset --"}</option>
                          {servicesCatalog.map((service) => (
                            <option key={service.id} value={service.id}>
                              {service.title} ({typeof service.price === "number" ? service.price.toFixed(2) : parseFloat(service.price).toFixed(2)} BDT)
                            </option>
                          ))}
                        </Form.Select>
                        {catalogLoading && (
                          <InputGroup.Text>
                            <Spinner animation="border" size="sm" variant="primary" />
                          </InputGroup.Text>
                        )}
                      </InputGroup>
                    </Form.Group>

                    <div className="cartesian-items-list mb-3 border rounded bg-light bg-opacity-25 p-2 overflow-y-auto" style={{ maxHeight: "240px" }}>
                      {selectedServices.length > 0 ? (
                        <Table borderless responsive size="sm" className="align-middle mb-0 small">
                          <thead>
                            <tr className="border-bottom text-muted" style={{ fontSize: "11px" }}>
                              <th>Description</th>
                              <th className="text-center" style={{ width: "80px" }}>Qty</th>
                              <th className="text-end">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedServices.map((item, index) => (
                              <tr key={item.business_item_id} className="border-bottom border-light">
                                <td className="text-truncate fw-medium" style={{ maxWidth: "110px" }}>{item.title}</td>
                                <td className="text-center">
                                  <div className="d-flex align-items-center justify-content-center border rounded bg-white px-1">
                                    <button type="button" className="btn btn-sm btn-link p-0 text-muted shadow-none border-0 bg-transparent" onClick={() => handleQuantityChange(index, -1)}>-</button>
                                    <span className="mx-2 fw-bold text-dark">{item.quantity}</span>
                                    <button type="button" className="btn btn-sm btn-link p-0 text-muted shadow-none border-0 bg-transparent" onClick={() => handleQuantityChange(index, 1)}>+</button>
                                  </div>
                                </td>
                                <td className="text-end fw-bold text-dark">{item.total.toFixed(2)} BDT</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      ) : (
                        <div className="p-4 text-muted small text-center fw-medium">No items added to dispatch checklist.</div>
                      )}
                    </div>

                    <div className="p-3 border rounded bg-light mb-4 text-center">
                      <span className="small text-uppercase text-muted d-block mb-1">Accounts Payable Subtotal</span>
                      <h4 className="fw-bold text-primary mb-0">{calculateGrossSubtotal().toFixed(2)} BDT</h4>
                    </div>

                    <div className="d-grid gap-2">
                      <Button type="submit" variant="primary" size="sm" disabled={submitting} className="fw-bold py-2">
                        {submitting ? <Spinner animation="border" size="sm" /> : "Complete & Dispatch Record"}
                      </Button>
                      <Button type="button" variant="outline-secondary" size="sm" disabled={submitting} onClick={() => navigate("/bookings")}>
                        Discard Form
                      </Button>
                    </div>
                  </Col>
                </Row>
              </Form>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}